import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Modal, Spin, Alert, Empty, Tooltip, Button, Space, Typography, Tag } from "antd";
import {
    CloseOutlined,
    ReloadOutlined,
    ColumnWidthOutlined,
    LeftOutlined,
    RightOutlined,
    FileOutlined,
} from "@ant-design/icons";
import { useInvoiceReview } from "../../context/InvoiceReviewContext";
import { getPresignedViewUrl } from "../../../../../api/attachmentApi";
import ReconciliationHeader from "./ReconciliationHeader";
import ReconciliationWorkspace from "./ReconciliationWorkspace";

const { Text } = Typography;

// 分隔栏宽度（像素）
const DIVIDER_WIDTH = 6;
// 左侧面板最小宽度比例
const MIN_LEFT_RATIO = 0.2;
// 左侧面板最大宽度比例
const MAX_LEFT_RATIO = 0.75;
// 默认左侧面板宽度比例
const DEFAULT_LEFT_RATIO = 0.45;

/**
 * 从 InvoiceDataSource 对象中提取可预览的文件列表。
 *
 * IDS 有两种记录类型：
 * - SINGLE_FILE: 单文件，OSS 路径存储在 raw_file_path 字段
 * - FILE_GROUP: 文件组，文件信息存储在 group_files JSONB 数组中
 *   每项格式: { object_key: 'path/file.pdf', filename: 'file.pdf' }
 *
 * @returns {Array<{objectKey: string, filename: string}>} 可预览的文件列表
 */
const extractFileList = (invoiceDataSource) => {
    if (!invoiceDataSource) return [];

    const recordType = invoiceDataSource.record_type || "SINGLE_FILE";

    if (recordType === "FILE_GROUP" && Array.isArray(invoiceDataSource.group_files)) {
        // 文件组：从 group_files 数组中提取每个文件
        return invoiceDataSource.group_files
            .filter((f) => f.object_key)
            .map((f) => ({
                objectKey: String(f.object_key).replace(/^\//, ""),
                filename: f.filename || f.object_key.split("/").pop() || "未知文件",
            }));
    }

    // 单文件：从 raw_file_path 获取
    const rawPath = invoiceDataSource.raw_file_path;
    if (!rawPath) return [];

    // raw_file_path 可能是完整 URL 或 OSS object_key
    let objectKey;
    if (/^https?:\/\//i.test(rawPath)) {
        try {
            const u = new URL(rawPath);
            objectKey = decodeURIComponent(u.pathname.replace(/^\//, ""));
        } catch {
            objectKey = rawPath.replace(/^\//, "");
        }
    } else {
        objectKey = String(rawPath).replace(/^\//, "");
    }

    return [
        {
            objectKey,
            filename: invoiceDataSource.original_filename || objectKey.split("/").pop() || "账单文件",
        },
    ];
};

/**
 * SplitViewModal — 分屏视图组件
 *
 * 全屏 Modal，左侧展示账单原始 PDF 文件（通过 iframe + 预签名 URL），
 * 右侧嵌入现有的 ReconciliationHeader + ReconciliationWorkspace。
 *
 * 设计要点：
 * - 不嵌套 Provider：直接消费外层 InvoiceReviewContext
 * - 可拖拽分隔栏：用户可调整左右面板宽度比例
 * - 支持 SINGLE_FILE 和 FILE_GROUP 两种记录类型
 * - 文件组模式下可切换不同文件
 * - PDF 预签名 URL 有时效性，提供手动刷新按钮
 */
const SplitViewModal = ({ visible, onClose, toggleDocumentViewer, documentViewerState, embedded = false }) => {
    const { invoiceDataSource, invoiceDataSourceId } = useInvoiceReview();

    // 从 IDS 提取文件列表
    const fileList = useMemo(() => extractFileList(invoiceDataSource), [invoiceDataSource]);

    // 当前选中的文件索引
    const [activeFileIndex, setActiveFileIndex] = useState(0);

    // PDF 预签名 URL 相关状态
    const [pdfUrl, setPdfUrl] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [pdfError, setPdfError] = useState(null);

    // 左侧面板宽度比例（0~1）
    const [leftRatio, setLeftRatio] = useState(DEFAULT_LEFT_RATIO);
    // 是否正在拖拽
    const isDraggingRef = useRef(false);
    // Modal 内容区域的 ref（用于计算拖拽位置）
    const containerRef = useRef(null);

    // 获取指定文件的预签名 URL
    const fetchPdfUrl = useCallback(async (fileIndex) => {
        const file = fileList[fileIndex];
        if (!file?.objectKey) {
            setPdfError("无法获取文件路径");
            return;
        }

        setPdfLoading(true);
        setPdfError(null);

        try {
            // 请求较长的过期时间（30分钟），避免频繁刷新
            const result = await getPresignedViewUrl(file.objectKey, 1800);
            if (result?.signed_url) {
                setPdfUrl(result.signed_url);
            } else {
                setPdfError("获取 PDF 预签名 URL 失败：返回数据异常");
            }
        } catch (error) {
            console.error("获取 PDF 预签名 URL 失败:", error);
            setPdfError(`获取 PDF 预签名 URL 失败: ${error.message || "未知错误"}`);
        } finally {
            setPdfLoading(false);
        }
    }, [fileList]);

    // Modal 打开时或文件切换时获取 PDF URL
    useEffect(() => {
        if (visible && fileList.length > 0) {
            // 确保 activeFileIndex 有效
            const safeIndex = Math.min(activeFileIndex, fileList.length - 1);
            if (safeIndex !== activeFileIndex) {
                setActiveFileIndex(safeIndex);
            }
            fetchPdfUrl(safeIndex);
        }
        // Modal 关闭时清理状态
        if (!visible) {
            setPdfUrl(null);
            setPdfError(null);
            setLeftRatio(DEFAULT_LEFT_RATIO);
            setActiveFileIndex(0);
        }
    }, [visible, activeFileIndex, fileList, fetchPdfUrl]);

    // 切换文件
    const handleFileSwitch = useCallback((index) => {
        setPdfUrl(null); // 先清空，避免显示上一个文件的残影
        setActiveFileIndex(index);
    }, []);

    // --- 拖拽分隔栏逻辑 ---
    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        isDraggingRef.current = true;
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
    }, []);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDraggingRef.current || !containerRef.current) return;

            const containerRect = containerRef.current.getBoundingClientRect();
            const containerWidth = containerRect.width;
            const mouseX = e.clientX - containerRect.left;

            let newRatio = (mouseX - DIVIDER_WIDTH / 2) / (containerWidth - DIVIDER_WIDTH);
            newRatio = Math.max(MIN_LEFT_RATIO, Math.min(MAX_LEFT_RATIO, newRatio));
            setLeftRatio(newRatio);
        };

        const handleMouseUp = () => {
            if (isDraggingRef.current) {
                isDraggingRef.current = false;
                document.body.style.cursor = "";
                document.body.style.userSelect = "";
            }
        };

        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);

        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, []);

    // 快捷操作
    const collapsePdf = () => setLeftRatio(MIN_LEFT_RATIO);
    const expandPdf = () => setLeftRatio(DEFAULT_LEFT_RATIO);
    const maximizePdf = () => setLeftRatio(MAX_LEFT_RATIO);

    // --- PDF 面板渲染 ---
    const renderPdfPanel = () => {
        if (pdfLoading) {
            return (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                    <Spin tip="正在加载 PDF 文件..." size="large">
                        <div style={{ minHeight: 100 }} />
                    </Spin>
                </div>
            );
        }

        if (pdfError) {
            return (
                <div style={{ padding: 24 }}>
                    <Alert
                        message="PDF 加载失败"
                        description={pdfError}
                        type="error"
                        showIcon
                        action={
                            <Button size="small" onClick={() => fetchPdfUrl(activeFileIndex)} icon={<ReloadOutlined />}>
                                重试
                            </Button>
                        }
                    />
                </div>
            );
        }

        if (fileList.length === 0) {
            return (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                    <Empty description="当前账单数据源无关联的 PDF 文件" />
                </div>
            );
        }

        if (!pdfUrl) {
            return (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                    <Spin tip="准备加载..." size="large">
                        <div style={{ minHeight: 100 }} />
                    </Spin>
                </div>
            );
        }

        return (
            <iframe
                src={pdfUrl}
                title="账单 PDF 预览"
                style={{
                    width: "100%",
                    height: "100%",
                    border: "none",
                }}
            />
        );
    };

    // 当前文件信息
    const activeFile = fileList[activeFileIndex];
    const isFileGroup = fileList.length > 1;

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            className="split-view-modal"
            closable={false}
            footer={null}
            destroyOnClose
            maskClosable={false}
            keyboard
            zIndex={1100}
        >
            {/* 顶部工具栏 */}
            <div className="split-view-toolbar">
                <Space>
                    <ColumnWidthOutlined style={{ fontSize: 16, color: "#1890ff" }} />
                    <Text strong style={{ fontSize: 14 }}>
                        分屏视图
                    </Text>
                    {activeFile && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            — {activeFile.filename}
                        </Text>
                    )}
                    {isFileGroup && (
                        <Tag color="orange" style={{ marginLeft: 4 }}>
                            文件组 ({fileList.length} 个文件)
                        </Tag>
                    )}
                </Space>
                <Space size="small">
                    {/* 文件组切换按钮 */}
                    {isFileGroup &&
                        fileList.map((file, idx) => (
                            <Tooltip key={idx} title={file.filename}>
                                <Button
                                    type={idx === activeFileIndex ? "primary" : "default"}
                                    size="small"
                                    icon={<FileOutlined />}
                                    onClick={() => handleFileSwitch(idx)}
                                    style={{ minWidth: 28 }}
                                >
                                    {idx + 1}
                                </Button>
                            </Tooltip>
                        ))}
                    {isFileGroup && (
                        <div style={{ width: 1, height: 16, background: "#d9d9d9", margin: "0 4px" }} />
                    )}
                    {/* PDF 面板宽度控制 */}
                    <Tooltip title="收窄 PDF 面板">
                        <Button type="text" size="small" icon={<LeftOutlined />} onClick={collapsePdf} />
                    </Tooltip>
                    <Tooltip title="等分面板">
                        <Button type="text" size="small" icon={<ColumnWidthOutlined />} onClick={expandPdf} />
                    </Tooltip>
                    <Tooltip title="放大 PDF 面板">
                        <Button type="text" size="small" icon={<RightOutlined />} onClick={maximizePdf} />
                    </Tooltip>
                    <div style={{ width: 1, height: 16, background: "#d9d9d9", margin: "0 4px" }} />
                    <Tooltip title="刷新 PDF">
                        <Button type="text" size="small" icon={<ReloadOutlined />} onClick={() => fetchPdfUrl(activeFileIndex)} loading={pdfLoading} />
                    </Tooltip>
                    <Tooltip title="关闭分屏 (ESC)">
                        <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClose} />
                    </Tooltip>
                </Space>
            </div>

            {/* 分屏内容区域 */}
            <div className="split-view-container" ref={containerRef}>
                {/* 左侧 — PDF 查看器 */}
                <div
                    className="split-view-left"
                    style={{ width: `calc(${leftRatio * 100}% - ${DIVIDER_WIDTH / 2}px)` }}
                >
                    {renderPdfPanel()}
                </div>

                {/* 可拖拽分隔栏 */}
                <div
                    className="split-view-divider"
                    style={{ width: DIVIDER_WIDTH }}
                    onMouseDown={handleMouseDown}
                >
                    <div className="split-view-divider-handle" />
                </div>

                {/* 右侧 — 对账工作区 */}
                <div
                    className="split-view-right"
                    style={{ width: `calc(${(1 - leftRatio) * 100}% - ${DIVIDER_WIDTH / 2}px)` }}
                >
                    {/* ReconciliationHeader：透传与页面组件一致的 props */}
                    <ReconciliationHeader
                        toggleDocumentViewer={toggleDocumentViewer}
                        documentViewerState={documentViewerState}
                        embedded={embedded}
                    />
                    {/* 主工作区 */}
                    <div className="split-view-workspace">
                        <div
                            style={{
                                background: "#fff",
                                borderRadius: "8px",
                                border: "1px solid #f0f0f0",
                                minHeight: "calc(100vh - 140px)",
                            }}
                        >
                            <ReconciliationWorkspace />
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default SplitViewModal;
